import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, ClipboardCheck, Ship } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { requireFlow } from '@/features/auth/guards'
import { BoatImageGallery } from '@/features/inventory/components/boat-image-gallery'
import { ENGINE_MANUFACTURERS } from '@/features/inventory/boat-form'
import { useInspection } from '@/features/inspection/hooks'

export const Route = createFileRoute('/_authenticated/inspection/$inspectionId')({
  beforeLoad: requireFlow('inspection'),
  component: InspectionDetailPage,
})

const ENGINE_LABELS = Object.fromEntries(ENGINE_MANUFACTURERS.map((m) => [m.value, m.label]))

function InspectionDetailPage() {
  const { inspectionId } = Route.useParams()
  const { data: insp, isLoading, error } = useInspection(inspectionId)

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }
  if (error || !insp) {
    return (
      <div className="flex flex-col items-center gap-3 p-12 text-center">
        <Ship className="h-10 w-10 text-muted-foreground" />
        <p className="font-medium">Inspection not found.</p>
        <Button variant="outline" asChild>
          <Link to="/inspection">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Inspection
          </Link>
        </Button>
      </div>
    )
  }

  const engineLine =
    insp.engine_count > 0
      ? [
          `${insp.engine_count} × ${insp.engine_brand ? (ENGINE_LABELS[insp.engine_brand] ?? insp.engine_brand) : 'engine'}`,
          insp.engine_model,
          insp.engine_serial ? `SN ${insp.engine_serial}` : '',
        ]
          .filter(Boolean)
          .join(' · ')
      : 'Unpowered'

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
          <Link to="/inspection">
            <ArrowLeft className="mr-2 h-4 w-4" /> Inspection
          </Link>
        </Button>
        <h1 className="flex flex-wrap items-center gap-2 text-3xl font-bold tracking-tight">
          <ClipboardCheck className="h-8 w-8" />
          {insp.year} {insp.make} {insp.model}
        </h1>
        <p className="text-muted-foreground">
          Inspected {new Date(insp.performed_at).toLocaleString()} ·{' '}
          <Link
            to="/inventory/$boatId"
            params={{ boatId: insp.boat_id }}
            className="underline-offset-4 hover:underline"
          >
            view boat in Inventory
          </Link>
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">HIN</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-mono text-sm tracking-wider">{insp.hin}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Engine</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">{engineLine}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Attached work order</CardTitle>
          </CardHeader>
          <CardContent>
            {insp.work_order_id ? (
              <Link
                to="/service/$workOrderId"
                params={{ workOrderId: insp.work_order_id }}
                className="text-sm font-medium underline-offset-4 hover:underline"
              >
                {insp.work_order_title || 'View work order'}
              </Link>
            ) : (
              <div className="text-sm text-muted-foreground">
                None — the boat had no open work orders.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {insp.notes && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{insp.notes}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Boat photos</CardTitle>
        </CardHeader>
        <CardContent>
          <BoatImageGallery boatId={insp.boat_id} />
        </CardContent>
      </Card>
    </div>
  )
}
